import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CalendarEventSummary, CaseSummary } from '../types';

/**
 * Modal for create/edit/delete of a single calendar event. The Calendar
 * page mounts this when the user clicks "+ אירוע" or an existing event.
 */
export function EventModal({
  initial,
  defaultDate,
  onSaved,
  onClose,
  onDeleted,
}: {
  // When editing an existing event
  initial?: CalendarEventSummary | null;
  // When creating from a clicked day cell
  defaultDate?: Date;
  onSaved: () => void;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation(['calendar', 'common']);
  const editing = !!initial;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [start, setStart] = useState(() => toLocalInput(initial?.start ?? defaultAt(defaultDate, 9)));
  const [end, setEnd] = useState(() => toLocalInput(initial?.end ?? defaultAt(defaultDate, 10)));
  const [caseId, setCaseId] = useState<string>(initial?.caseId ?? '');
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull case list once for the "link to case" dropdown
  useEffect(() => {
    apiFetch<{ items: CaseSummary[] }>('/api/cases')
      .then((d) => setCases(d.items))
      .catch(() => setCases([]));
  }, []);

  // Keep end ≥ start: if user pushes start past end, bump end by 1h
  useEffect(() => {
    if (new Date(end) <= new Date(start)) {
      const nextEnd = new Date(new Date(start).getTime() + 60 * 60 * 1000);
      setEnd(toLocalInput(nextEnd.toISOString()));
    }
  }, [start, end]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        caseId: caseId || null,
      };
      if (editing && initial) {
        await apiFetch(`/api/calendar/events/${initial.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/calendar/events', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown';
      const errorKey = `errors.${code}`;
      const translated = t(errorKey);
      setError(translated === errorKey ? t('common:errors.with_message', { message: code }) : translated);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing || !initial) return;
    if (!confirm(t('form.delete_confirm', { title: initial.title }))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/calendar/events/${initial.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete_failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {editing ? t('form.edit_title') : t('new_event')}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="field">
                <label>{t('form.title')} <span className="req">*</span></label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder={t('form.title_placeholder')}
                  autoFocus
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('form.start')} <span className="req">*</span></label>
                <input
                  type="datetime-local"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('form.end')} <span className="req">*</span></label>
                <input
                  type="datetime-local"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('form.location')}</label>
                <input value={location} onChange={(e) => setLocation(e.target.value)} />
              </div>
              <div className="field">
                <label>{t('form.case')}</label>
                <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                  <option value="">{t('form.no_case')}</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} {c.client?.name ? `· ${c.client.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('form.description')}</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('form.submit_busy') : t('form.submit')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            {editing && onDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-danger"
                style={{ marginInlineStart: 'auto' }}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Convert an ISO string to the value format <input type="datetime-local">
 * expects: "YYYY-MM-DDTHH:mm" in *local* time. Date.toISOString gives UTC,
 * so we have to re-derive the local components.
 */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** ISO string for the given date at the given hour (or now+1h if no date). */
function defaultAt(d: Date | undefined, hour: number): string {
  if (d) {
    const x = new Date(d);
    x.setHours(hour, 0, 0, 0);
    return x.toISOString();
  }
  const x = new Date();
  x.setHours(x.getHours() + 1, 0, 0, 0);
  if (hour === 10) x.setHours(x.getHours() + 1);
  return x.toISOString();
}
